import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { Box, Grid2 as Grid, Typography, CardHeader as MuiCardHeader } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactSVG } from 'react-svg';

import { Icons } from '../../../Themes';
import { moment } from '../../../I18n';
import Avatar from '../Avatar';
import Chip from '../Chip';

const StyledVerifiedSpace = styled(ReactSVG)(({ theme }: any) => ({
  width: 15,
  height: 15,
  marginLeft: theme.spacing(1.5),
  '& path': {
    fill: theme.palette.secondary.main,
  },
}));

function CardHeader({
  isAuthor = false,
  action = null,
  alt,
  avatarSize = 48,
  badge = null,
  date = undefined,
  subheader = null,
  extra,
  fullName,
  initials = '',
  intl,
  isVerified = false,
  placeholderType = 'user',
  profilePictureUrl = null,
}: any) {
  const renderPublishState = () => {
    if (date === undefined) {
      return null;
    }

    if (moment(date).isValid()) {
      return (
        <Typography
          variant="body2"
          sx={{
            '&::first-letter': {
              textTransform: 'uppercase',
            },
          }}
        >
          {moment(date).fromNow()}
        </Typography>
      );
    }

    return intl.formatMessage({ id: 'ARTICLE.NOT_PUBLISHED' });
  };

  return (
    <MuiCardHeader
      sx={{ padding: 0 }}
      avatar={
        <Avatar
          src={profilePictureUrl}
          badge={badge}
          alt={alt}
          size={avatarSize}
          placeholderType={placeholderType}
          fullName={fullName}
          initials={initials}
        />
      }
      title={
        <Grid container sx={{ alignItems: 'center', marginX: 1.5 }}>
          <Typography
            variant="body1"
            color="textSecondary"
            sx={{
              fontWeight: 'bold',
              textTransform: 'uppercase',
              fontSize: 13,
              letterSpacing: 0.5,
            }}
          >
            {fullName}
          </Typography>
          {isVerified && <StyledVerifiedSpace src={Icons.entities.verified} />}
          {isAuthor && (
            <Box sx={{ ml: 1.5 }}>
              <Chip
                label={intl.formatMessage({ id: 'COMMENTS.AUTHOR' })}
                typographyComponent="h3"
                color="secondary"
                variant="outlined"
                size="small"
              />
            </Box>
          )}
        </Grid>
      }
      subheader={
        <Box
          sx={{
            gap: 0.5,
            display: 'inline-flex',
            alignItems: 'baseline',
            justifyContent: 'start',
            mx: 1.5,
          }}
        >
          {subheader}
          {subheader && date !== undefined && ' - '}
          {renderPublishState()}
          {extra}
        </Box>
      }
      action={action}
    />
  );
}

CardHeader.propTypes = {
  isAuthor: PropTypes.bool,
  isVerified: PropTypes.bool,
  fullName: PropTypes.string.isRequired,
  profilePictureUrl: PropTypes.string,
  date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  subheader: PropTypes.string,
  placeholderType: PropTypes.oneOf(['user', 'entity']),
  badge: PropTypes.node,
  alt: PropTypes.string.isRequired,
  action: PropTypes.node,
  avatarSize: PropTypes.number,
  initials: PropTypes.string,
};

export default injectIntl(CardHeader);
